import { EngineFormItem } from '@db-access/clientIntakeFormItems';
import { ClientIntakeResponse } from '@db-access/clientIntakeResponses';
import { IntakeFormContextSelectors } from '@domain/intake-form-context';
import {
	ClientIntakeForm,
	ClientIntakeFormContext,
} from '@models/client-intake';
import { createSelector } from '@ngxs/store';
import { buildContextData } from '../utils/portal-engine.utils';
import { IntakeFormClientPortalSelectors } from '@domain/intake-form-client-portal';
import { IntakeFormItemsSelectors } from '@domain/intake-form-items';
import { IntakeFormClientResponsesSelectors } from '@domain/intake-form-client-responses';
import { Form } from '@models/client-intake';
import { RulesEngineService } from '@domain/rule-definitions';
import { getFormContent, orderFormContent } from '../utils/portal-engine.utils';
import { PortalEngineData } from './portal-engine.models';

export class PortalEngineSelectors {
	static readonly $engineData = createSelector(
		[
			IntakeFormClientPortalSelectors.$currentIntakeForm,
			IntakeFormItemsSelectors.$intakeFormItems,
			IntakeFormContextSelectors.$context,
			IntakeFormClientResponsesSelectors.$intakeFormResponses,
		],
		(
			currentForm: ClientIntakeForm | null,
			engineItems: EngineFormItem[],
			context: ClientIntakeFormContext,
			currentResponses: ClientIntakeResponse[],
		): PortalEngineData | null => {
			if (!currentForm?.ruleDefinition || !engineItems.length) {
				return null;
			}

			// TEMPORARY: can remove or keep this once we decide if we want to delete old client responses
			const clientsContext = context.clients?.all.map((client) => client.id);
			const filteredCurrentResponses = currentResponses.filter(
				(response) =>
					clientsContext?.includes(response.id) || response.type === 'general',
			);

			return {
				context,
				currentForm,
				currentResponses: filteredCurrentResponses,
				engineItems,
			};
		},
	);

	static readonly $engineGeneratedForms = createSelector(
		[PortalEngineSelectors.$engineData],
		(engineData: PortalEngineData | null): Form[] | null => {
			if (
				!engineData?.currentForm?.ruleDefinition ||
				!engineData?.engineItems.length ||
				!engineData?.context ||
				!engineData?.currentResponses.length
			) {
				return null;
			}

			const { context, currentForm, currentResponses, engineItems } =
				engineData;

			// Build context data similar to client-portal
			const contextData = buildContextData(currentResponses, context);

			if (contextData.clients?.all?.length === 0 || !contextData.general) {
				return null;
			}

			// Execute rules engine (we'll need to pass the service instance)
			const rulesEngineService = new RulesEngineService();
			const results = rulesEngineService.execute(
				currentForm.ruleDefinition,
				contextData,
				engineItems,
			);

			const formContent = getFormContent(results);
			const forms = orderFormContent(formContent);

			return forms;
		},
	);
}
